import { useEffect, useState } from 'react';

import { getTabAssignment } from '@/auth/activeAssignment';
import { ApiProvider } from '@/provider/ApiProvider';

import { getEffectiveAccess } from './api';
import {
  type Capabilities,
  PERMISSION_USER_EDIT,
  capabilitiesFrom,
  mayReadEffectiveAccess,
} from './capabilities';

/**
 * Who may change an organisation's SSO settings (gateway /sso/*):
 *
 * - Settings > Manage Users > Company sign-in (SSO): the INTERNAL
 *   organisation's settings, platform owners only (403
 *   platform_owner_required). Shown for an INTERNAL active role once
 *   effective-access says is_platform_owner.
 * - Settings > Your organization > Login Preferences: a customer
 *   organisation's settings, for platform owners and that organisation's
 *   admins (an active assignment in it whose role holds user_edit); everyone
 *   else gets 403 sso_settings_forbidden (owner decision 2026-10-07). Shown at
 *   once for a customer active role with user_edit, otherwise once
 *   effective-access says is_platform_owner.
 *
 * effective-access is read at most once per active assignment and page load,
 * shared by both links and the Login Preferences page, and only for a role
 * that holds an access administration key (it refuses everyone else who is
 * not a platform owner): without one the answer is "nothing to administer",
 * without a call.
 */
export const COMPANY_SSO_PATH = '/company-sso';
export const LOGIN_PREFERENCES_PATH = '/loginpreferences';

const CUSTOMER_CONTEXTS = ['BRAND', 'RIGHT_HOLDER', 'AGENCY'];

let capsCheck: { assignment: number | null; promise: Promise<Capabilities | null> } | null = null;

const activeAssignment = () => getTabAssignment();

const roleHasKey = (key: string): boolean => Boolean(ApiProvider.permission(key));

/** The caller's capabilities for the active assignment (null: effective-access failed). */
function capabilitiesLater(): Promise<Capabilities | null> {
  // effective-access would refuse (403 permission_required): nothing to administer
  if (!mayReadEffectiveAccess(roleHasKey)) return Promise.resolve(capabilitiesFrom(null, false));
  const id = activeAssignment()?.id ?? null;
  if (!capsCheck || capsCheck.assignment !== id) {
    capsCheck = {
      assignment: id,
      promise: getEffectiveAccess().then(
        (r) => (r.ok ? capabilitiesFrom(r.data) : null),
        () => null,
      ),
    };
  }
  return capsCheck.promise;
}

export function companySsoLinkLater(): Promise<boolean> {
  if (activeAssignment()?.contextType !== 'INTERNAL') return Promise.resolve(false);
  return capabilitiesLater().then((caps) => Boolean(caps?.isInternal && caps.isPlatformOwner));
}

/**
 * A customer organisation's admin, as far as the UI knows without a call: a
 * BRAND / RIGHT_HOLDER / AGENCY active role holding user_edit (the
 * permissions map holds the active role's permissions). The server checks
 * that the assignment is in the organisation being changed.
 */
export function ssoSettingsByPermission(): boolean {
  const type = activeAssignment()?.contextType;
  return (
    !!type &&
    CUSTOMER_CONTEXTS.includes(type) &&
    Boolean(ApiProvider.permission(PERMISSION_USER_EDIT))
  );
}

/**
 * May the caller change a customer organisation's SSO settings? true / false,
 * or null when effective-access could not be read (the server decides). No
 * call when the permission rule already answers.
 */
export function ssoSettingsAccessLater(): Promise<boolean | null> {
  if (ssoSettingsByPermission()) return Promise.resolve(true);
  return capabilitiesLater().then((caps) => (caps ? caps.isPlatformOwner : null));
}

export function loginPreferencesLinkLater(): Promise<boolean> {
  return ssoSettingsAccessLater().then((v) => v === true);
}

/**
 * Settings > Manage Users > Organisations (owner decision 2026-10-07):
 * platform owners and holders of the Application Admin role in an INTERNAL
 * active role. Shown at once for an INTERNAL role holding
 * security_rules_manage (the Application Admin system role holds every
 * permission), otherwise once effective-access says is_platform_owner. The
 * server decides (403 organisation_admin_required).
 */
export function organisationsLinkNow(): boolean {
  return (
    activeAssignment()?.contextType === 'INTERNAL' &&
    Boolean(ApiProvider.permission('security_rules_manage'))
  );
}

export function organisationsLinkLater(): Promise<boolean> {
  if (activeAssignment()?.contextType !== 'INTERNAL') return Promise.resolve(false);
  return capabilitiesLater().then((caps) => Boolean(caps?.isPlatformOwner));
}

export const resetCompanySsoLinkForTests = (): void => {
  capsCheck = null;
};

export type SsoSettingsAccess = 'checking' | 'allowed' | 'forbidden' | 'unknown';

/**
 * The Login Preferences page: may the caller manage a customer
 * organisation's SSO settings? The INTERNAL organisation (company sign-in)
 * is left to the server (platform_owner_required), as before. 'unknown':
 * effective-access failed, the server decides.
 */
export function useSsoSettingsAccess(internal: boolean): SsoSettingsAccess {
  const [state, setState] = useState<SsoSettingsAccess>(() =>
    internal || ssoSettingsByPermission() ? 'allowed' : 'checking',
  );
  useEffect(() => {
    if (internal) {
      setState('allowed');
      return;
    }
    let alive = true;
    void ssoSettingsAccessLater().then((v) => {
      if (alive) setState(v === null ? 'unknown' : v ? 'allowed' : 'forbidden');
    });
    return () => {
      alive = false;
    };
  }, [internal]);
  return state;
}
